"use client";

import { useActionState } from "react";
import { z } from "zod";

import { FieldError } from "@/components/shared/field";
import { track } from "@/lib/analytics/data-layer";
import {
  newsletterSubscribeSchema,
  type NewsletterSubscribeValues,
} from "@/lib/newsletter/newsletter-schemas";

const FIELD_CLASS =
  "flex-1 border-none bg-transparent px-0.5 py-3.5 text-[16px] leading-[normal] " +
  "text-white outline-none placeholder:text-[rgba(255,255,255,0.50)] " +
  // The ink the browser's autofill must keep (`globals.css`, browser autofill): it forces its own
  // black otherwise, unreadable on the closing band.
  "[--tb-field-ink:var(--tb-white)]";

const FIELD_ROW_CLASS = "flex border-b border-[rgba(255,255,255,0.66)]";

/** Errors sit over photography, so pure white rather than the destructive red. */
const FIELD_ERROR_CLASS = "text-white";

type SubscribeState = {
  subscribed: boolean;
  errors: Partial<Record<keyof NewsletterSubscribeValues, string>>;
  /**
   * Echoed back because React resets the form once the action returns. Feeding
   * these to `defaultValue` is what survives that reset.
   */
  values: NewsletterSubscribeValues;
};

const INITIAL_STATE: SubscribeState = {
  subscribed: false,
  errors: {},
  values: { name: "", email: "" },
};

/** Values arrive as `FormData`, so the schema the BFF will reuse stays the only validator. */
function subscribe(_previous: SubscribeState, formData: FormData): SubscribeState {
  const values = {
    name: String(formData.get("name") ?? ""),
    email: String(formData.get("email") ?? ""),
  };

  const parsed = newsletterSubscribeSchema.safeParse(values);

  if (!parsed.success) {
    const { fieldErrors } = z.flattenError(parsed.error);

    return {
      subscribed: false,
      errors: { name: fieldErrors.name?.[0], email: fieldErrors.email?.[0] },
      values,
    };
  }

  // Counts a lead the product does not keep: no receiving service exists yet, so
  // this reports a conversion with nothing behind it (docs § Tagueamento). Moves
  // to the BFF response when the route lands.
  track({ event: "generate_lead", method: "newsletter", location: "newsletter" });

  return { subscribed: true, errors: {}, values };
}

/**
 * Newsletter — the page's only write, capturing name + e-mail.
 *
 * Renders transparent: the photo, parallax and darkening gradient belong to
 * `NewsletterFooterBand`, which spans this section and the footer. Height comes
 * from the band's `--tb-circle-h`.
 */
export function NewsletterSection() {
  const [state, formAction] = useActionState(subscribe, INITIAL_STATE);

  return (
    <section id="circulo" className="relative z-[1] min-h-[var(--tb-circle-h)]">
      <div className="flex min-h-[var(--tb-circle-h)] flex-col justify-between gap-[clamp(70px,10vh,140px)] p-[clamp(20px,4vw,56px)] pb-[clamp(28px,3.4vw,44px)]">
        <span className="text-[15px] font-medium uppercase tracking-[0.16em] text-white">Assine nossa newsletter</span>

        <div className="flex w-full flex-col gap-[clamp(20px,2.6vw,32px)] [@media(min-width:900px)]:w-[min(620px,44%)] [@media(min-width:900px)]:self-end">
          <h2 className="font-display text-[clamp(21px,2.11vw,33px)] font-light uppercase leading-[1.24] tracking-[-0.005em] text-white [text-wrap:pretty]">
            As coleções que nascem no atelier, as peças que ganham a mesa e a arte de receber em casa: o universo Tania Bulhões, no seu e-mail.
          </h2>

          {state.subscribed ? (
            <p
              // The form unmounts rather than cross-fading: kept mounted it would
              // stay reachable by Tab and announced by screen readers.
              className="text-[16px] leading-[1.6] text-white animate-[tb-rise-in_var(--tb-duration-3)_var(--tb-ease-quill)] motion-reduce:animate-none"
            >
              Pronto. Nossa próxima nota chega até você.
            </p>
          ) : (
            <form action={formAction} noValidate className="flex w-full flex-col gap-1.5">
              {/* The reference has no visible labels, so each field is named by
                  `aria-label` — a placeholder alone leaves it unnamed. */}
              <div className="flex flex-col">
                <span className={FIELD_ROW_CLASS}>
                  <input
                    id="newsletter-name"
                    name="name"
                    defaultValue={state.values.name}
                    type="text"
                    autoComplete="name"
                    aria-label="Nome"
                    placeholder="Digite o seu nome"
                    aria-invalid={!!state.errors.name}
                    aria-describedby="newsletter-name-error"
                    className={FIELD_CLASS}
                  />
                </span>
                <FieldError id="newsletter-name-error" className={FIELD_ERROR_CLASS}>
                  {state.errors.name}
                </FieldError>
              </div>

              <div className="flex flex-col">
                <span className={FIELD_ROW_CLASS}>
                  <input
                    id="newsletter-email"
                    name="email"
                    defaultValue={state.values.email}
                    type="email"
                    autoComplete="email"
                    aria-label="E-mail"
                    placeholder="Digite o seu e-mail"
                    aria-invalid={!!state.errors.email}
                    aria-describedby="newsletter-email-error"
                    className={FIELD_CLASS}
                  />
                  <button
                    type="submit"
                    className="cursor-pointer border-none bg-transparent px-1 py-0 text-[15px] leading-[normal] text-white transition-opacity duration-[400ms] hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2"
                  >
                    Assinar
                  </button>
                </span>
                <FieldError id="newsletter-email-error" className={FIELD_ERROR_CLASS}>
                  {state.errors.email}
                </FieldError>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
